import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { applicationsApi } from "../api/applications";
import type { Application } from "../api/types";
import StatusBadge from "../components/StatusBadge";
import { Button, Spinner } from "../components/ui";

export default function Applications() {
  const { data, isPending } = useQuery({
    queryKey: ["applications"],
    queryFn: () => applicationsApi.list(),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Applications</h1>
          <p className="mt-1 text-sm text-gray-600">
            Every job you have generated documents for.
          </p>
        </div>
        <Link to="/new">
          <Button>New application</Button>
        </Link>
      </header>

      {isPending && <Spinner label="Loading applications…" />}
      {data && data.length === 0 && <EmptyState />}
      {data && data.length > 0 && (
        <ul className="space-y-3">
          {data.map((application) => (
            <li key={application.id}>
              <ApplicationRow application={application} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-surface p-10 text-center">
      <h2 className="text-base font-medium text-gray-900">Nothing here yet</h2>
      <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
        Paste a job ad and Claude will write you a tailored resume and cover letter.
      </p>
      <Link to="/new" className="mt-4 inline-block">
        <Button>Add your first job ad</Button>
      </Link>
    </div>
  );
}

function ApplicationRow({ application }: { application: Application }) {
  return (
    <Link
      to={`/applications/${application.id}`}
      className="block rounded-lg border border-gray-200 bg-surface p-4 shadow-xs transition-colors hover:border-accent-500"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-900">
            {application.role || "Untitled role"}
          </p>
          <p className="mt-0.5 truncate text-sm text-gray-600">
            {[application.company, application.location].filter(Boolean).join(" · ") ||
              "Still reading the ad…"}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {application.fit_score !== null && <FitScore score={application.fit_score} />}
          <StatusBadge status={application.status} />
        </div>
      </div>
    </Link>
  );
}

export function FitScore({ score }: { score: number }) {
  // Green when it is worth applying for, amber when marginal, grey when not.
  const tone =
    score >= 7
      ? "bg-accent-50 text-accent-700"
      : score >= 5
        ? "bg-amber-50 text-amber-700"
        : "bg-gray-100 text-gray-600";

  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>
      Fit {score.toFixed(1)}
    </span>
  );
}
